// Utilitaires pour les messages système

/**
 * Retourne une date relative en français
 * @param {import('firebase/firestore').Timestamp|Date} timestamp
 * @returns {string} Ex: "Il y a 2 jours"
 */
export const getRelativeTime = (timestamp) => {
  if (!timestamp) return '';

  let date;
  if (timestamp?.toDate) {
    date = timestamp.toDate();
  } else if (timestamp instanceof Date) {
    date = timestamp;
  } else {
    return '';
  }

  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  const diffWeeks = Math.floor(diffDays / 7);
  const diffMonths = Math.floor(diffDays / 30);

  if (diffMins < 1) return "À l'instant";
  if (diffMins < 60) return `Il y a ${diffMins} min`;
  if (diffHours < 24) return `Il y a ${diffHours}h`;
  if (diffDays < 7) return `Il y a ${diffDays} jour${diffDays > 1 ? 's' : ''}`;
  if (diffWeeks < 5) return `Il y a ${diffWeeks} semaine${diffWeeks > 1 ? 's' : ''}`;
  return `Il y a ${diffMonths} mois`;
};

/**
 * Retourne l'emoji correspondant au type de message
 * @param {'feature'|'news'|'maintenance'|'promo'|'update'} type
 * @returns {string}
 */
export const getMessageIcon = (type) => {
  const icons = {
    feature: '🆕',
    news: '📢',
    maintenance: '🔧',
    promo: '💎',
    update: '⚠️',
  };
  return icons[type] || '📬';
};

/**
 * Retourne la couleur hex correspondant au type de message
 * @param {'feature'|'news'|'maintenance'|'promo'|'update'} type
 * @returns {string}
 */
export const getMessageColor = (type) => {
  const colors = {
    feature: '#4CAF50',
    news: '#2196F3',
    maintenance: '#FF9800',
    promo: '#9C27B0',
    update: '#F44336',
  };
  return colors[type] || '#6B7280';
};

/**
 * Retourne la classe Tailwind de fond correspondant au type
 * @param {'feature'|'news'|'maintenance'|'promo'|'update'} type
 * @returns {string}
 */
export const getMessageBgClass = (type) => {
  const classes = {
    feature: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300',
    news: 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300',
    maintenance: 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300',
    promo: 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300',
    update: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300',
  };
  return classes[type] || 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300';
};

/**
 * Retourne le libellé français du type
 * @param {'feature'|'news'|'maintenance'|'promo'|'update'} type
 * @returns {string}
 */
export const getMessageTypeLabel = (type) => {
  const labels = {
    feature: 'Nouveauté',
    news: 'Actualité',
    maintenance: 'Maintenance',
    promo: 'Promotion',
    update: 'Important',
  };
  return labels[type] || type;
};

/**
 * Retourne le libellé français de la priorité
 * @param {'low'|'normal'|'high'} priority
 * @returns {string}
 */
export const getPriorityLabel = (priority) => {
  const labels = {
    low: 'Basse',
    normal: 'Normale',
    high: 'Haute',
  };
  return labels[priority] || priority;
};

/**
 * Retourne le libellé français de l'audience cible
 * @param {'all'|'free'|'premium'} audience
 * @returns {string}
 */
export const getAudienceLabel = (audience) => {
  const labels = {
    all: 'Tous les utilisateurs',
    free: 'Utilisateurs gratuits',
    premium: 'Utilisateurs premium',
  };
  return labels[audience] || audience;
};

/**
 * Extrait un texte court depuis du HTML pour l'aperçu
 * @param {string} html
 * @param {number} maxLength
 * @returns {string}
 */
export const extractTextFromHtml = (html, maxLength = 120) => {
  if (!html) return '';
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  const text = tmp.textContent || tmp.innerText || '';
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength).trim() + '...';
};
